import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const STATUS = {
  progression: ["En progression", "text-emerald-300", TrendingUp],
  stable: ["Stable", "text-[var(--parchment)]", Minus],
  baisse: ["En baisse", "text-red-300", TrendingDown],
  non_positionne: ["Non positionné", "text-[var(--muted)]", Minus],
  pas_assez_de_donnees: ["Données insuffisantes", "text-[var(--muted)]", Minus],
};
const short = (u) => (u || "").replace("https://ozentya.net", "") || "/";
const num = (v) => (v ?? 0).toLocaleString("fr-FR");

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="text-[var(--muted)]">—</span>;
  if (Math.abs(v) < 0.1) return <span>=</span>;
  const better = v < 0;
  return <span className={better ? "text-emerald-300" : "text-red-300"}>{better ? "▲" : "▼"} {Math.abs(v).toFixed(1)}</span>;
}

function Connect({ r }) {
  return (
    <div data-testid="seo-not-connected" className="rounded-sm border border-[var(--gold)]/50 p-6 text-sm leading-relaxed">
      <p className="font-display text-2xl italic text-[var(--gold-soft)]">Search Console n'est pas encore connecté</p>
      <p className="mt-3 text-[var(--parchment)]/85">Aucune position n'est affichée tant que Google ne nous transmet pas de données réelles. Pour connecter :</p>
      <ol className="mt-3 list-decimal space-y-1 pl-5 text-[var(--parchment)]/85">
        <li>Google Cloud : créez un compte de service avec l'API « Google Search Console API » activée, puis téléchargez sa clé JSON.</li>
        <li>Search Console → Paramètres → Utilisateurs et autorisations → ajoutez l'email du compte de service (« Utilisateur limité »){r.client_email ? <> : <b className="break-all">{r.client_email}</b></> : null}.</li>
        <li>Collez le contenu du fichier JSON dans le secret <code>GSC_SERVICE_ACCOUNT_JSON</code>, puis republiez.</li>
      </ol>
      {r.last_error ? <p data-testid="seo-last-error" className="mt-3 text-red-300">Dernière erreur : {r.last_error}</p> : null}
    </div>
  );
}

function KeywordTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="seo-keywords-table" className="w-full min-w-[920px] text-left text-sm">
        <thead className="text-xs uppercase tracking-[0.14em] text-[var(--muted)]">
          <tr>{["Mot-clé", "Statut", "Position (7 j)", "Dernière mesure", "7 jours", "30 jours", "Page positionnée", "Impr. 28 j", "Clics", "CTR", "Volume"].map((h) => <th key={h} className="border-b border-[var(--line)] py-3 pr-4 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((k) => {
            const [label, cls, Icon] = STATUS[k.status] || STATUS.non_positionne;
            return (
              <tr key={k.keyword} data-testid={`seo-kw-${k.keyword.replace(/\s+/g, "-")}`} className="border-b border-[var(--line)]">
                <td className="py-3 pr-4 font-medium">{k.keyword}</td>
                <td className={`py-3 pr-4 ${cls}`}><span className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5" />{label}</span></td>
                <td className="py-3 pr-4 font-display text-xl">{k.position ?? "—"}</td>
                <td className="py-3 pr-4"><Delta v={k.delta_last} /></td>
                <td className="py-3 pr-4"><Delta v={k.delta_7d} /></td>
                <td className="py-3 pr-4"><Delta v={k.delta_30d} /></td>
                <td className="py-3 pr-4 text-[var(--gold-soft)]">{k.page ? short(k.page) : "—"}</td>
                <td className="py-3 pr-4">{num(k.impressions)}</td>
                <td className="py-3 pr-4">{num(k.clicks)}</td>
                <td className="py-3 pr-4">{k.impressions ? `${k.ctr} %` : "—"}</td>
                <td className="py-3 pr-4 text-[var(--muted)]">{k.volume ?? "n.d."}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function List({ title, rows, kind, testid, empty }) {
  return (
    <section data-testid={testid} className="rounded-sm border border-[var(--line)] p-5">
      <h3 className="font-display text-xl italic text-[var(--gold-soft)]">{title}</h3>
      {rows.length ? (
        <ul className="mt-3 space-y-2 text-sm">
          {rows.map((r) => (
            <li key={r[kind]} className="flex flex-wrap justify-between gap-2 border-b border-[var(--line)] pb-2">
              <span className="break-all">{kind === "page" ? short(r.page) : r.query}</span>
              <span className="text-[var(--parchment)]/80">pos. {r.position ?? "—"} · {num(r.impressions)} impr. · {num(r.clicks)} clics · {r.ctr} %{r.delta !== undefined ? <> · <Delta v={r.delta} /></> : null}</span>
            </li>
          ))}
        </ul>
      ) : <p className="mt-3 text-sm text-[var(--muted)]">{empty}</p>}
    </section>
  );
}

function KeywordsEditor({ keywords }) {
  const qc = useQueryClient();
  const [text, setText] = useState(keywords.join("\n"));
  const save = async () => {
    try {
      await api.put("/admin/seo/keywords", { keywords: text.split("\n") });
      toast.success("Mots-clés enregistrés");
      qc.invalidateQueries({ queryKey: ["seo-report"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <details className="rounded-sm border border-[var(--line)] p-5">
      <summary data-testid="seo-keywords-edit-toggle" className="cursor-pointer text-sm uppercase tracking-[0.16em] text-[var(--gold-soft)]">Modifier les mots-clés suivis</summary>
      <textarea data-testid="seo-keywords-input" rows={10} value={text} onChange={(e) => setText(e.target.value)} className="mt-4 w-full rounded-sm border border-[var(--line)] bg-[var(--ink)] p-3 text-sm" />
      <button data-testid="seo-keywords-save" onClick={save} className="mt-3 rounded-full bg-[var(--gold)] px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)]">Enregistrer</button>
    </details>
  );
}

export function SeoTracker() {
  const qc = useQueryClient();
  const { data: r, isLoading } = useQuery({ queryKey: ["seo-report"], queryFn: async () => (await api.get("/admin/seo/report")).data });
  const sync = async () => {
    try {
      await api.post("/admin/seo/sync");
      toast.success("Synchronisation lancée, rechargez dans une minute.");
      setTimeout(() => qc.invalidateQueries({ queryKey: ["seo-report"] }), 30000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading || !r) return <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />;
  return (
    <div data-testid="seo-tracker" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-3xl">Suivi des positions Google</p>
          <p className="mt-1 text-sm text-[var(--muted)]">Source : Google Search Console uniquement (données réelles, publiées par Google avec 2 à 3 jours de retard).{r.has_data ? ` Période : ${r.period[0]} → ${r.period[1]}.` : ""}{r.property ? ` Propriété : ${r.property}.` : ""}</p>
          {r.last_run ? <p className="text-xs text-[var(--muted)]">Dernière synchronisation : {new Date(r.last_run).toLocaleString("fr-FR")}</p> : null}
        </div>
        {r.configured ? <button data-testid="seo-sync-button" onClick={sync} className="flex items-center gap-2 rounded-full border border-[var(--gold)] px-5 py-2 text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]"><RefreshCw className="h-3.5 w-3.5" /> Synchroniser</button> : null}
      </div>
      {!r.configured || !r.has_data ? (r.configured ? <p data-testid="seo-no-data" className="text-sm">Connecté. Aucune donnée encore récupérée : cliquez sur « Synchroniser ».{r.last_error ? ` Erreur : ${r.last_error}` : ""}</p> : <Connect r={r} />) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-sm border border-[var(--line)] p-5"><p className="eyebrow">Impressions 28 j</p><p data-testid="seo-total-impressions" className="font-display text-4xl">{num(r.total.impressions)}</p></div>
            <div className="rounded-sm border border-[var(--line)] p-5"><p className="eyebrow">Clics 28 j</p><p data-testid="seo-total-clicks" className="font-display text-4xl">{num(r.total.clicks)}</p></div>
          </div>
          <KeywordTable rows={r.tracked} />
          <p className="text-xs text-[var(--muted)]">Position : moyenne Google des 7 derniers jours (1 = premier résultat). ▲ = gain de places, ▼ = perte. Volume de recherche : non fourni par Search Console.</p>
          <div className="grid gap-6 lg:grid-cols-2">
            <List title="Quick wins : requêtes en positions 4 à 20" rows={r.opportunities} kind="query" testid="seo-opportunities" empty="Aucune requête dans cette zone pour l'instant." />
            <List title="Beaucoup d'impressions, peu de clics (CTR < 2 %)" rows={r.low_ctr} kind="query" testid="seo-low-ctr" empty="Rien à signaler." />
            <List title="Pages les plus visibles (28 j)" rows={r.pages.slice(0, 12)} kind="page" testid="seo-pages" empty="Aucune page." />
            <List title="Pages qui progressent" rows={r.pages_gaining} kind="page" testid="seo-pages-gaining" empty="Aucune progression mesurée." />
            <List title="Pages qui perdent des positions" rows={r.pages_losing} kind="page" testid="seo-pages-losing" empty="Aucune baisse mesurée." />
            <List title="Requêtes les plus vues (28 j)" rows={r.top_queries.slice(0, 15)} kind="query" testid="seo-top-queries" empty="Aucune requête." />
          </div>
        </>
      )}
      <KeywordsEditor keywords={r.keywords} />
    </div>
  );
}
